import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useMarketplace";
import { useLoginDialog } from "@/contexts/LoginDialogContext";
import { useSignupDialog } from "@/contexts/SignupDialogContext";
import {
  LogOut, Crown, Settings, User, Sun, Moon, ChevronDown, Search, Shield, Zap, LayoutDashboard, Bell, Bot, Plane, Gavel,
} from "lucide-react";
import NotificationBell from "@/components/notifications/NotificationBell";
import { CurrencySelector } from "@/components/money";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { useProfile } from "@/hooks/useProfile";
import { SidebarContext } from "@/components/ui/sidebar";
import { useContext } from "react";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useSiteSetting } from "@/hooks/useSiteSettings";

const SidebarToggleButton = () => {
  const ctx = useContext(SidebarContext);
  if (!ctx) return null;
  const isOpen = ctx.state === "expanded";
  return (
    <button
      onClick={ctx.toggleSidebar}
      aria-label={isOpen ? "Collapse sidebar" : "Expand sidebar"}
      aria-expanded={isOpen}
      className="hidden md:inline-flex h-9 w-9 mr-2 md:mr-4 -ml-1 rounded-full hover:bg-muted items-center justify-center text-foreground/80 transition-colors"
    >
      {isOpen ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeftOpen className="w-5 h-5" />}
    </button>
  );
};

const navLinks = ["Home", "Market Hub", "Video Meetings"];

// Module-scoped singleton registry: ensures only ONE <Navbar /> renders at a time
// even if both the global App-level navbar and a legacy per-page <Navbar /> mount.
const navbarRegistry = { count: 0 };

const MAIN_PAGES = [
  "/", "/feed", "/dashboard", "/marketplace", "/chat", "/profile", "/news", "/predictions",
  "/currency-flip", "/groups", "/forum", "/events", "/dating",
  "/meetings", "/jobs", "/contract-bids", "/my-bids", "/wallet",
  "/logistics", "/engage", "/skill-trades",
  "/legal-directory", "/business-directory", "/accounting", "/advertise",
  "/settings", "/subscription", "/hall", "/virtual-market", "/raffles", "/escrow",
  "/invitations", "/resume-ai", "/login", "/signup", "/elections",
  "/food-court", "/beauty", "/digital-cards",
  "/bank-connect", "/campus", "/work-safety",
  "/projects", "/qr-generator", "/file-sharing",
  "/freelance", "/trading", "/quiz-verse", "/quizverse", "/bible-wall",
  "/staysphere", "/interview-ai", "/admin", "/giving",
  "/job-insights", "/meal-planner",
];

const UserMenu = ({ user, profile, initial, isAdmin, dark, setDark, signOut }: any) => {
  const navigate = useNavigate();
  return (
    <DropdownMenuContent align="end" className="w-48">
      {isAdmin ? (
        <DropdownMenuItem asChild>
          <Link to="/admin" className="gap-2">
            <Shield className="w-4 h-4" /> Admin Dashboard
          </Link>
        </DropdownMenuItem>
      ) : (
        <DropdownMenuItem asChild>
          <Link to="/dashboard" className="gap-2">
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </Link>
        </DropdownMenuItem>
      )}
      <DropdownMenuItem asChild>
        <Link to={`/profile/${user.id}`} className="gap-2">
          <User className="w-4 h-4" /> My Profile
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/my-bids" className="gap-2">
          <Gavel className="w-4 h-4" /> My Bids
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/settings/notifications" className="gap-2">
          <Bell className="w-4 h-4" /> Notifications
        </Link>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => setDark(!dark)} className="gap-2">
        {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        {dark ? "Light Mode" : "Dark Mode"}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={signOut} className="gap-2 text-destructive focus:text-destructive">
        <LogOut className="w-4 h-4" /> Log Out
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
};

const Navbar = () => {
  const { user, signOut } = useAuth();
  const { openLoginDialog } = useLoginDialog();
  const { openSignupDialog } = useSignupDialog();
  const { data: registrationDisabled } = useSiteSetting("registration_disabled");
  const { data: profile } = useProfile(user?.id);
  const { data: roles } = useUserRole();
  const navigate = useNavigate();
  const isAdmin = roles?.includes("admin") ?? false;
  // Singleton guard — if a Navbar is already mounted (e.g. the global one in App),
  // any additional <Navbar /> rendered inside a page is suppressed to avoid stacking.
  const [allowed] = useState(() => {
    if (navbarRegistry.count > 0) return false;
    navbarRegistry.count++;
    return true;
  });
  useEffect(() => {
    if (!allowed) return;
    return () => { navbarRegistry.count = Math.max(0, navbarRegistry.count - 1); };
  }, [allowed]);
  const [dark, setDark] = useState(() =>
    typeof window !== "undefined" && document.documentElement.classList.contains("dark")
  );
  const [filterSearch, setFilterSearch] = useState("");

  if (!allowed) return null;

  const location = useLocation();
  const isLanding = location.pathname === "/";
  

  const searchPlaceholder = (() => {
    const p = location.pathname;
    if (p.startsWith("/feed") || p.startsWith("/profile")) return "Search posts...";
    if (p.startsWith("/news")) return "Search news...";
    if (p.startsWith("/events")) return "Search events...";
    if (p.startsWith("/jobs") || p.startsWith("/job-board")) return "Search jobs...";
    if (p.startsWith("/freelance")) return "Search gigs...";
    if (p.startsWith("/groups")) return "Search groups...";
    if (p.startsWith("/forum")) return "Search threads...";
    if (p.startsWith("/dating")) return "Search profiles...";
    if (p.startsWith("/auto")) return "Search vehicles...";
    if (p.startsWith("/property")) return "Search properties...";
    if (p.startsWith("/skill-trades")) return "Search trades...";
    if (p.startsWith("/legal-directory")) return "Search lawyers...";
    if (p.startsWith("/business-directory")) return "Search businesses...";
    if (p.startsWith("/staysphere")) return "Search stays...";
    if (p.startsWith("/raffles")) return "Search raffles...";
    if (p.startsWith("/predictions")) return "Search predictions...";
    if (p.startsWith("/quiz-verse")) return "Search quizzes...";
    if (p.startsWith("/bible-wall")) return "Search the Bible...";
    if (p.startsWith("/campus")) return "Search campus...";
    if (p.startsWith("/digital-cards")) return "Search cards...";
    if (p.startsWith("/food-court")) return "Search food...";
    if (p.startsWith("/beauty")) return "Search beauty...";
    if (p.startsWith("/giving")) return "Search causes...";
    if (p.startsWith("/elections")) return "Search elections...";
    if (p.startsWith("/blog")) return "Search blog...";
    if (p.startsWith("/projects")) return "Search projects...";
    if (p.startsWith("/wallet")) return "Search transactions...";
    if (p.startsWith("/travel")) return "Search travel...";
    return "Search marketplace...";
  })();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  // Live search: results populate as the user types (no need to press the
  // search button). Debounced so we don't navigate on every keystroke.
  useEffect(() => {
    const q = filterSearch.trim();
    if (!q) return;
    const t = setTimeout(() => {
      const target = getSearchTarget(location.pathname);
      navigate(`${target}?q=${encodeURIComponent(q)}`, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSearch]);

  const initial = (profile?.display_name?.[0] || user?.email?.[0] || "A").toUpperCase();

  const handleBack = () => {
    if (window.history.length > 2) navigate(-1);
    else navigate("/feed");
  };

  // Route the global search to the page the user is currently on so it
  // filters that page's content. Falls back to the marketplace search.
  const getSearchTarget = (path: string): string => {
    // Exact-match table for top-level pages with their own search/?q= param.
    const exact: Record<string, string> = {
      "/feed": "/feed",
      "/marketplace": "/marketplace",
      "/news": "/news",
      "/events": "/events",
      "/jobs": "/jobs",
      "/job-board": "/job-board",
      "/freelance": "/freelance",
      "/groups": "/groups",
      "/forum": "/forum",
      "/dating": "/dating",
      "/skill-trades": "/skill-trades",
      "/legal-directory": "/legal-directory",
      "/business-directory": "/business-directory",
      "/staysphere": "/staysphere",
      "/raffles": "/raffles",
      "/predictions": "/predictions",
      "/quiz-verse": "/quiz-verse",
      "/bible-wall": "/bible-wall",
      "/campus": "/campus",
      "/digital-cards": "/digital-cards",
      "/food-court": "/food-court",
      "/beauty": "/beauty",
      "/giving": "/giving",
      "/elections": "/elections",
      "/blog": "/blog",
      "/projects": "/projects",
      "/wallet": "/wallet",
    };
    if (exact[path]) return exact[path];
    // Prefix matches for nested routes — search the parent listing page.
    if (path.startsWith("/profile")) return "/feed";
    if (path.startsWith("/auto")) return "/marketplace";
    if (path.startsWith("/property")) return "/marketplace";
    if (path.startsWith("/staysphere")) return "/staysphere";
    if (path.startsWith("/campus")) return "/campus";
    if (path.startsWith("/elections")) return "/elections";
    if (path.startsWith("/blog")) return "/blog";
    if (path.startsWith("/travel")) return "/travel/deals";
    // Default to marketplace.
    return "/marketplace";
  };

  const handleFilterSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = filterSearch.trim();
    if (!q) return;
    const target = getSearchTarget(location.pathname);
    navigate(`${target}?q=${encodeURIComponent(q)}`);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/60 md:border-b-0">
      <div className="w-full max-w-none px-4 md:container md:px-3 flex items-center justify-between h-[min(56px,100vh)] md:h-[56px] md:h-[56px]">
        <div className="flex items-center gap-1.5 md:gap-2">
          <SidebarToggleButton />
          <Link to={user ? "/feed" : "/"} className="flex items-center gap-2 font-display text-[26px] md:text-[28px] font-extrabold gradient-text drop-shadow-sm leading-tight tracking-tight">
            <picture>
              <source srcSet="/logo.webp" type="image/webp" />
              <img
                src="/logo.png"
                alt="Gloogum logo"
                width={36}
                height={32}
                fetchPriority="high"
                decoding="async"
                className="w-8 h-8 md:w-9 md:h-9 object-contain"
              />
            </picture>
            <span>Gloogum</span>
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            const routeMap: Record<string, string> = { "Home": "/feed", "Market Hub": "/marketplace", "Video Meetings": "/meetings" };
            const route = routeMap[link] || `/${link.toLowerCase()}`;
            return (
              <Link key={link} to={route} className="text-[15px] font-semibold text-foreground/80 hover:text-foreground transition-colors">
                {link}
              </Link>
            );
          })}
        </div>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              {!isLanding && (
                <>
                  <NotificationBell />
                </>
              )}
              <CurrencySelector compact triggerClassName="h-8 w-[92px] text-xs" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Avatar className="w-8 h-8">
                      {profile?.avatar_url && <AvatarImage src={profile.avatar_url} />}
                      <AvatarFallback className="text-xs bg-primary/10 text-primary font-bold">{initial}</AvatarFallback>
                    </Avatar>
                    <span className="max-w-[min(100px,100vw)] md:max-w-[100px] truncate text-sm">{profile?.display_name || "My Account"}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <UserMenu user={user} profile={profile} initial={initial} isAdmin={isAdmin} dark={dark} setDark={setDark} signOut={signOut} />
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={openLoginDialog}>Log In</Button>
              <Button
                variant="hero"
                size="sm"
                onClick={openSignupDialog}
                disabled={registrationDisabled}
                title={registrationDisabled ? "Registration is temporarily disabled" : undefined}
              >
                Sign Up
              </Button>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="md:hidden flex items-center gap-2">
          {user ? (
            <>
              <NotificationBell />
              <button
                aria-label="Open Gloogum Assistant"
                onClick={() => window.dispatchEvent(new CustomEvent("open-gloogum-assistant"))}
                className="h-9 w-9 rounded-full hover:bg-muted transition-colors flex items-center justify-center"
              >
                <Bot strokeWidth={1.75} className="w-5 h-5 text-foreground/80" />
              </button>
              {/* User icon with dropdown submenu on mobile */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button aria-label="Account" className="rounded-full hover:opacity-90 transition-opacity ring-1 ring-border/70">
                    <Avatar className="w-8 h-8">
                      {profile?.avatar_url && <AvatarImage src={profile.avatar_url} />}
                      <AvatarFallback className="text-xs bg-primary/10 text-primary font-bold">{initial}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <UserMenu user={user} profile={profile} initial={initial} isAdmin={isAdmin} dark={dark} setDark={setDark} signOut={signOut} />
              </DropdownMenu>
            </>
          ) : (
            <>
              <button aria-label="Notifications" className="h-9 w-9 rounded-full hover:bg-muted transition-colors flex items-center justify-center">
                <Bell strokeWidth={1.75} className="w-[min(22px,100vw)] md:w-[22px] h-[min(22px,100vh)] md:h-[22px] text-foreground/80" />
              </button>
              <button
                aria-label="Open Gloogum Assistant"
                onClick={() => window.dispatchEvent(new CustomEvent("open-gloogum-assistant"))}
                className="h-9 w-9 rounded-full hover:bg-muted transition-colors flex items-center justify-center"
              >
                <Bot strokeWidth={1.75} className="w-5 h-5 text-foreground/80" />
              </button>
              <button
                onClick={openLoginDialog}
                className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
              >
                Login
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
